import React, { useEffect, useRef } from "react";
import { pickColor, rgba } from "./internal/colorUtils";

export const key = "matrix-rain";

const DEFAULT_BG = "#000000";
const DEFAULT_TAIL = "#22c55e";
const DEFAULT_HEAD = "#bbf7d0";

// Mix of katakana, half-width katakana, digits, and a few latin/punct
// glyphs — that's what the original Matrix screen used. Pulled live from
// each column so the falling text never repeats.
const GLYPHS =
  "ｱｲｳｴｵｶｷｸｹｺｻｼｽｾｿﾀﾁﾂﾃﾄﾅﾆﾇﾈﾉﾊﾋﾌﾍﾎﾏﾐﾑﾒﾓﾔﾕﾖﾗﾘﾙﾚﾛﾜｦﾝ" +
  "0123456789Z<>+-:|=#{}*";

const FONT_SIZE = 14;          // px (logical)
const TAIL_FADE = 0.06;        // bg overlay alpha (lower = longer trails)
const SPAWN_PROB = 0.010;      // probability a dormant column starts a new drop per frame

function randomGlyph() {
  return GLYPHS[(Math.random() * GLYPHS.length) | 0];
}

export default function MatrixRain({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const colorsRef = useRef([DEFAULT_BG, DEFAULT_TAIL, DEFAULT_HEAD]);
  const colsRef = useRef([]);

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_BG),
      pickColor(colors, 1, DEFAULT_TAIL),
      pickColor(colors, 2, DEFAULT_HEAD),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;
    let primed = false;

    const seedColumns = () => {
      const colCount = Math.max(1, Math.floor(width / FONT_SIZE));
      const arr = new Array(colCount);
      for (let i = 0; i < colCount; i += 1) {
        arr[i] = makeCol(true);
      }
      colsRef.current = arr;
    };

    // A column is either active (dropping) or dormant (waiting to start).
    // Active columns track a "head" Y (pixel) and a per-step glyph.
    const makeCol = (initiallyDormant) => ({
      active: !initiallyDormant && Math.random() < 0.4,
      headY: -Math.random() * height,
      // Each column drops at a different speed → the rain is staggered.
      // Tuned for a calmer, more readable Matrix rain.
      speed: 0.3 + Math.random() * 0.6,
      glyph: randomGlyph(),
      // How often (in frames) the column rolls a new glyph at the head.
      // Higher values → glyphs flicker less hectically.
      glyphCadence: 4 + Math.floor(Math.random() * 6),
      cadenceCounter: 0,
    });

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      // Use clientWidth/Height (layout dims) so an ancestor `transform: scale(...)`
      // (e.g. PreviewDeviceStage) doesn't shrink the canvas and pin it to top-left.
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.font = `${FONT_SIZE}px monospace`;
      ctx.textBaseline = "top";
      seedColumns();
      primed = false;
    };

    const tick = () => {
      const [bg, tail, head] = colorsRef.current;

      // Translucent fill over the whole canvas → trail/fade effect that
      // gives every column a smooth comet-like falloff for free.
      if (!primed) {
        ctx.fillStyle = bg;
        ctx.fillRect(0, 0, width, height);
        primed = true;
      } else {
        ctx.fillStyle = rgba(bg, TAIL_FADE);
        ctx.fillRect(0, 0, width, height);
      }

      const cols = colsRef.current;
      ctx.font = `${FONT_SIZE}px monospace`;
      ctx.textBaseline = "top";

      for (let i = 0; i < cols.length; i += 1) {
        const c = cols[i];
        if (!c.active) {
          if (Math.random() < SPAWN_PROB) {
            c.active = true;
            c.headY = -FONT_SIZE * (1 + Math.random() * 6);
            c.speed = 0.3 + Math.random() * 0.6;
            c.glyph = randomGlyph();
          }
          continue;
        }

        c.cadenceCounter += 1;
        if (c.cadenceCounter >= c.glyphCadence) {
          c.cadenceCounter = 0;
          c.glyph = randomGlyph();
        }

        const x = i * FONT_SIZE;

        // Bright "head" character.
        ctx.fillStyle = head;
        ctx.fillText(c.glyph, x, c.headY);

        // The character one step behind the head — slightly dimmer than the
        // head but brighter than the trailing fade. Random glyph so the
        // streak isn't just two repeats of the head.
        if (c.headY > 0) {
          ctx.fillStyle = rgba(tail, 0.85);
          ctx.fillText(randomGlyph(), x, c.headY - FONT_SIZE);
        }

        c.headY += FONT_SIZE * c.speed * 0.5;

        if (c.headY > height + FONT_SIZE * 6) {
          c.active = false;
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
